import { createSentryPiniaPlugin } from '@sentry/vue';
import { toRaw, unref } from 'vue';

import { NOISY_PINIA_ACTIONS, PINIA_STATE_ALLOWLIST } from './pinia-state-allowlist';

/** Long enough to be useful, short enough that nothing document-shaped survives it. */
const MAX_STRING_LENGTH = 120;

/** Dot paths deeper than this are a sign someone is trying to walk a document tree. */
const MAX_PATH_SEGMENTS = 3;

const hasOwn = (target, key) => Object.prototype.hasOwnProperty.call(target, key);

/**
 * Reads one property while stepping outside Vue's reactive proxies. `toRaw` avoids registering a
 * dependency - this runs inside `$onAction.after`, which is reachable from within an active
 * effect - and `unref` unwraps the refs Pinia holds for setup-style stores.
 */
const read = (source, key) => {
	const raw = toRaw(source);

	if (raw === null || typeof raw !== 'object') {
		return undefined;
	}

	return toRaw(unref(raw[key]));
};

const readPath = (source, path) => {
	const segments = path.split('.', MAX_PATH_SEGMENTS + 1);

	if (segments.length > MAX_PATH_SEGMENTS) {
		return undefined;
	}

	return segments.reduce((current, segment) => read(current, segment), source);
};

/** Describes a non-scalar without reading any of its contents. */
const describe = (value) => {
	try {
		if (Array.isArray(value)) {
			return `[Array(${value.length})]`;
		}

		if (typeof value === 'function') {
			return '[Function]';
		}

		if (typeof value === 'symbol') {
			return '[Symbol]';
		}

		if (typeof value === 'bigint') {
			return `${value}n`;
		}

		if (value instanceof Date) {
			return Number.isNaN(value.getTime()) ? '[Invalid Date]' : value.toISOString();
		}

		return `[${value?.constructor?.name ?? 'Object'}]`;
	} catch {
		return '[Object]';
	}
};

/**
 * Coerces any value to a single JSON-safe scalar. There is no recursive branch, which is what
 * makes the allowlist durable: an allowlisted field that later becomes an object or an array
 * renders as a shape marker instead of leaking its contents.
 */
const toScalar = (value) => {
	if (value === null || value === undefined) {
		return null;
	}

	const type = typeof value;

	if (type === 'boolean') {
		return value;
	}

	if (type === 'number') {
		return Number.isFinite(value) ? value : String(value);
	}

	if (type === 'string') {
		return value.length > MAX_STRING_LENGTH
			? `${value.slice(0, MAX_STRING_LENGTH)}... [truncated, ${value.length} chars]`
			: value;
	}

	return describe(value);
};

const sizeOf = (value) => {
	try {
		if (Array.isArray(value)) {
			return value.length;
		}

		if (value instanceof Map || value instanceof Set) {
			return value.size;
		}

		if (value === null || typeof value !== 'object') {
			return 0;
		}

		return Object.keys(value).length;
	} catch {
		return -1;
	}
};

const summarizeStore = (storeState, entry) => {
	const summary = {};

	(entry.fields ?? []).forEach((path) => {
		try {
			const value = readPath(storeState, path);

			if (value !== undefined) {
				summary[path] = toScalar(value);
			}
		} catch {
			summary[path] = '[unreadable]';
		}
	});

	(entry.counts ?? []).forEach((path) => {
		try {
			const value = readPath(storeState, path);

			if (value !== undefined && value !== null) {
				summary[`${path}Count`] = sizeOf(value);
			}
		} catch {
			summary[`${path}Count`] = '[unreadable]';
		}
	});

	return summary;
};

/**
 * EN-961: rebuilds the Pinia state map from an explicit allowlist. Never recurses, so circular
 * state (`pdf-builder.router`, `tiptap.editor`, `bullet-generator`'s nested stores) cannot hang
 * or overflow the stack, and the per-action cost stays O(1) rather than growing with
 * `pdf-builder.history`.
 *
 * MUST NOT THROW. `getAllStoreStates` in @sentry/vue wraps this call in
 * `try { ... } catch { return states; }` - it fails OPEN and would ship the raw unfiltered state
 * of every store. The try/catch guards below are load-bearing; do not remove them.
 */
export const piniaStateTransformer = (states) => {
	try {
		const safe = {};
		const unallowlisted = [];

		Object.keys(states ?? {}).forEach((storeId) => {
			if (!hasOwn(PINIA_STATE_ALLOWLIST, storeId)) {
				unallowlisted.push(storeId);
				return;
			}

			const summary = summarizeStore(states[storeId], PINIA_STATE_ALLOWLIST[storeId]);

			if (Object.keys(summary).length > 0) {
				safe[storeId] = summary;
			}
		});

		if (unallowlisted.length > 0) {
			safe._unallowlistedStores = unallowlisted;
		}

		return safe;
	} catch {
		return { _piniaStateError: 'transform failed' };
	}
};

const PINIA_BREADCRUMB_PATTERN = /^Store: (.+) \| Action: (.+)$/;

/**
 * The message format is parsed rather than assumed, so a wording change upstream degrades to
 * "keep the breadcrumb" instead of dropping everything.
 */
export const isNoisyPiniaBreadcrumb = (breadcrumb) => {
	if (breadcrumb?.category !== 'pinia.action') {
		return false;
	}

	const match = PINIA_BREADCRUMB_PATTERN.exec(breadcrumb.message ?? '');

	return match !== null && NOISY_PINIA_ACTIONS.has(`${match[1]}|${match[2]}`);
};

/**
 * The only supported way to install the Sentry Pinia plugin. Calling `createSentryPiniaPlugin()`
 * bare attaches every store's full state to every event - that is EN-961.
 *
 * `attachPiniaState: false` because attachments bypass `beforeSend`, `normalizeDepth`,
 * `normalizeMaxBreadth` and `maxValueLength` entirely, duplicate the state context, are built
 * before `beforeSend` gets a chance to discard the event, and register one permanent global
 * event processor per store.
 *
 * `actionTransformer` is left at the SDK default: it receives only the action name, not the
 * store id, so a name-based denylist would suppress `save`/`init`/`reset` across every store.
 * Breadcrumb noise is filtered in `beforeBreadcrumb` instead.
 */
export const createPiniaSentryPlugin = () => {
	return createSentryPiniaPlugin({
		attachPiniaState: false,
		addBreadcrumbs: true,
		stateTransformer: piniaStateTransformer,
	});
};
